import { create } from 'zustand';
import { eventsModule } from '@/events/events-module';

/**
 * Chatbot message shape — mirrors Angular's `HeaderChatbotService.fullChatHistory` items.
 */
export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  questionId?: number;
  typing: boolean;
  isLiked: boolean;
  isDisLiked: boolean;
  isStatusMessage?: boolean;
  followUpQuestions?: string[];
  /** Timestamp captured at message creation (mirrors Angular's per-message time display). */
  timestamp?: string;
}

interface ChatbotState {
  messages: ChatMessage[];
  setMessages: (msgs: ChatMessage[]) => void;
  appendMessage: (msg: ChatMessage) => void;
  clearMessages: () => void;
  revealLastMessage: () => void;
  removeStatusMessages: () => void;
  updateMessage: (index: number, partial: Partial<ChatMessage>) => void;
}

/**
 * Zustand store for chatbot history
 * singleton (`fullChatHistory`). Persists messages across panel open/close cycles.
 *
 * Cleared on:
 *   - Client change (clientId change via subscriber below)
 *   - Client refresh (`shell:refresh-clients` event — EventTypes.CLIENT.REFRESH)
 *   - User explicit clear (trash icon)
 */
export const useChatbotStore = create<ChatbotState>((set) => ({
  messages: [],
  setMessages: (msgs) => set({ messages: msgs }),
  appendMessage: (msg) => set((s) => ({ messages: [...s.messages, msg] })),
  clearMessages: () => set({ messages: [] }),
  revealLastMessage: () =>
    set((s) => {
      const msgs = s.messages;
      if (msgs.length === 0) return s;
      const last = msgs[msgs.length - 1];
      if (!last.typing) return s;
      return { messages: [...msgs.slice(0, -1), { ...last, typing: false }] };
    }),
  removeStatusMessages: () =>
    set((s) => ({ messages: s.messages.filter((m) => !m.isStatusMessage) })),
  updateMessage: (index, partial) =>
    set((s) => {
      const updated = [...s.messages];
      updated[index] = { ...updated[index], ...partial };
      return { messages: updated };
    }),
}));

// ── Side-effects: clear on refresh (EventTypes.CLIENT.REFRESH)
eventsModule.on('shell:refresh-clients', () => {
  useChatbotStore.getState().clearMessages();
});
